<template>
  <section role="listitem" :aria-label="eventTitle">
    <article class="AiEvent Layout-section">
      <div class="AiEvent-date-content">
        <ai-date :date="startDate" />
        <ai-typo
          v-if="endDate"
          class="AiEvent-date-content--separator"
          as="span"
          variant="overline-02">
          {{ $t(`hotel.event.label`) }}
        </ai-typo>
        <ai-date v-if="endDate" :date="endDate" />
      </div>
      <div class="AiEvent-description-content">
        <div class="AiEvent-image">
          <ai-image
            v-if="image?.src"
            :src="image.src"
            :alt="image.alt ?? $t('misc.imageAlt.event', { title: eventTitle })"
            :src-set="image.srcSet as string"
            loading="lazy" />
        </div>
        <div class="AiEvent-description">
          <ai-typo
            class="AiEvent-description--title"
            as="h3"
            variant="heading-03">
            {{ eventTitle }}
          </ai-typo>
          <ai-typo
            class="AiEvent-description--location"
            as="p"
            variant="overline-01">
            {{ location }}
          </ai-typo>
          <ai-typo
            class="AiEvent-description--description"
            as="p"
            variant="paragraph-04-regular">
            {{ eventDescription }}
          </ai-typo>
        </div>
      </div>
    </article>
  </section>
  <ai-separator />
</template>

<script setup lang="ts">
import type { AiPicture } from '~~/domains/graphql';

export interface AiEventProps {
  endDate?: string;
  eventDescription?: string;
  eventTitle?: string;
  location?: string;
  startDate?: string;
  image?: AiPicture;
}

defineProps<AiEventProps>();
</script>
<style lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/grid';

.AiEvent {
  flex-wrap: nowrap;
}

.AiEvent-date-content {
  @include grid.cell(calc(12 / 4 * 1));

  @media (mq.$from-medium) {
    display: flex;
    align-items: center;

    @include grid.cell(calc(12 / 6 * 2));
    @include mixins.rem-fallback(max-height, 90);
  }
  &--separator {
    color: colors.$gold-900;
    margin-top: constants.$inner-02;
    text-transform: uppercase;
    @media (mq.$from-medium) {
      margin: 0 constants.$inner-01;
    }
  }
}

.AiEvent-image {
  margin-bottom: constants.$margin-02;
  @include mixins.rem-fallback(max-width, 222);

  @media (mq.$from-large) {
    margin-bottom: 0;
    margin-right: constants.$margin-02;
  }
  img {
    @include mixins.rem-fallback(min-width, 222);
    @include mixins.rem-fallback(height, 184);
  }
}

.AiEvent-description {
  margin: 0;

  &--title {
    margin-bottom: constants.$inner-01;
  }
  &--location {
    text-transform: uppercase;
    margin-bottom: constants.$margin-01;
    color: colors.$stratos-500;
  }
  &--description {
    margin-bottom: constants.$margin-03;
  }
}

.AiEvent-description-content {
  @include grid.cell(calc(12 / 4 * 3));
  & {
    margin: 0;
  }

  @media (mq.$from-large) {
    display: flex;
    @include grid.cell(calc(12 / 6 * 5));
  }
}
</style>
